import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Mic, Heart, Users, BookOpen, GraduationCap, Shield, HeartHandshake, Activity, Sparkles, Mail, Phone } from "lucide-react";
import Seo from "../lib/seo";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import PetitionCTA from "../components/PetitionCTA";
import { BOARD_MEMBERS } from "../lib/constants";

const CIERA_REAL = "/images/ciera-portrait.png";

// Bio item with red outline icon
const BioItem = ({ icon: Icon, children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, x: -24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
        className="grid grid-cols-[auto_1fr] gap-6 items-start py-7 border-b border-editorial-border last:border-b-0"
    >
        <motion.div
            whileHover={{ scale: 1.08, rotate: -3 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className="w-16 h-16 border-2 border-coral rounded-full flex items-center justify-center bg-white shadow-sm"
        >
            <Icon className="w-7 h-7 text-coral" strokeWidth={1.6} />
        </motion.div>
        <p className="text-lg md:text-[1.08rem] text-navy-800/85 leading-relaxed pt-3">
            {children}
        </p>
    </motion.div>
);

// Animated heartbeat ECG line
const HeartbeatLine = ({ className = "" }) => (
    <svg viewBox="0 0 400 30" className={`w-full h-5 ${className}`} fill="none" aria-hidden>
        <motion.path
            d="M0 15 L80 15 L100 15 L110 5 L120 25 L130 8 L140 22 L150 15 L240 15 L260 15 L270 5 L280 25 L290 8 L300 22 L310 15 L400 15"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2.2, ease: "easeInOut" }}
        />
    </svg>
);

export default function FounderPage() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 120]);

    const schema = {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Ciera Gingery",
        jobTitle: "Founder, JC Law Foundation",
        description: "Founder of the JC Law Initiative, a public-safety advocacy movement dedicated to improving cardiac emergency preparedness in hotels and lodging facilities.",
        worksFor: { "@type": "NGO", name: "JC Law Foundation" },
    };

    return (
        <>
            <Seo
                title="Founder — Ciera Gingery"
                description="Meet Ciera Gingery, founder of the JC Law Foundation. Her mission: turn pain into prevention and create safer environments for travelers and families everywhere."
                path="/founder"
                image={CIERA_REAL}
                schema={schema}
            />

            <PageHeader
                overline="Founder · Founder & Advocate"
                title="Ciera Gingery"
                subtitle="Turning pain into prevention — one community, one conversation, one second at a time."
            />

            {/* Main bio section — magazine spread */}
            <section className="bg-editorial py-24 md:py-32 relative overflow-hidden" data-testid="founder-bio">
                <div className="absolute inset-0 bg-noise opacity-20 pointer-events-none" />
                <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start relative">
                    {/* LEFT — Portrait card */}
                    <motion.aside
                        ref={heroRef}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="lg:col-span-5 lg:sticky lg:top-28"
                    >
                        <div className="relative group">
                            {/* Decorative frame */}
                            <motion.div
                                className="absolute -inset-3 border border-gold/40 -z-10"
                                initial={{ scale: 0.95, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                            />
                            <motion.div style={{ y: parallaxY }} className="relative overflow-hidden bg-editorial-muted shadow-2xl">
                                <img
                                    src={CIERA_REAL}
                                    alt="Ciera Gingery — Founder and Advocate, JC Law Foundation"
                                    className="w-full h-auto block transition-transform duration-[2000ms] group-hover:scale-105"
                                    loading="lazy"
                                />
                            </motion.div>
                            {/* Floating heartbeat badge */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.6 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.8, type: "spring" }}
                                className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-coral text-white flex items-center justify-center shadow-xl"
                            >
                                <Heart className="w-9 h-9 animate-heartbeat" fill="currentColor" />
                            </motion.div>
                        </div>
                    </motion.aside>

                    {/* RIGHT — Founder Bio content */}
                    <div className="lg:col-span-7">
                        {/* Heading with red & navy split treatment */}
                        <Reveal>
                            <div className="text-center lg:text-left">
                                <p className="overline mb-4 text-coral inline-block">Founder Bio</p>
                                <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-navy-900">
                                    Founder <span className="text-coral">Bio</span>
                                </h2>
                                <div className="flex items-center gap-4 my-8">
                                    <div className="flex-1 h-px bg-coral/40" />
                                    <motion.div
                                        whileHover={{ scale: 1.15 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                        className="w-10 h-10 rounded-full border-2 border-coral flex items-center justify-center bg-white"
                                    >
                                        <Heart className="w-4 h-4 text-coral animate-heartbeat" fill="currentColor" />
                                    </motion.div>
                                    <div className="flex-1 h-px bg-coral/40" />
                                </div>
                            </div>
                        </Reveal>

                        {/* Bio items with red icons */}
                        <div className="bg-white p-2 md:p-4">
                            <BioItem icon={Activity} delay={0.05}>
                                <strong className="text-coral font-semibold">Ciera Gingery</strong> is the founder of the JC Law Initiative, a public-safety advocacy movement dedicated to improving cardiac emergency preparedness in hotels and lodging facilities.
                            </BioItem>
                            <BioItem icon={Shield} delay={0.1}>
                                Inspired by personal tragedy and driven by a passion for prevention, Ciera launched JC Law to advocate for legislation requiring CPR-trained hotel staff and accessible AED devices in lodging facilities. Her mission is rooted in the belief that immediate emergency response can save lives and that no family should experience preventable loss due to lack of preparedness.
                            </BioItem>
                            <BioItem icon={GraduationCap} delay={0.15}>
                                With a background in healthcare and patient care leadership, Ciera combines firsthand experience, medical knowledge, and public advocacy to raise awareness surrounding cardiac emergencies, CPR education, and AED accessibility.
                            </BioItem>
                            <BioItem icon={Users} delay={0.2}>
                                In addition to her advocacy work, Ciera is an upcoming author, speaker, and host of <em className="text-coral">"Unfiltered with Ciera,"</em> where she shares stories centered around resilience, healing, trauma, grief, and purpose.
                            </BioItem>
                            <BioItem icon={HeartHandshake} delay={0.25}>
                                Through JC Law, Ciera hopes to turn pain into prevention by creating safer environments for travelers and families everywhere.
                            </BioItem>
                        </div>

                        {/* EVERY SECOND MATTERS divider */}
                        <Reveal delay={0.3} className="mt-12">
                            <div className="h-px bg-coral mb-8" />
                            <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-navy-900 text-center">
                                Every Second <span className="text-coral">Matters.</span>
                            </h3>
                            <div className="text-coral mt-4 mb-2"><HeartbeatLine /></div>
                            <div className="h-px bg-coral" />
                        </Reveal>

                        {/* CTAs */}
                        <Reveal delay={0.35}>
                            <div className="flex flex-col sm:flex-row gap-3 pt-10">
                                <Link to="/our-story" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-navy-900 hover:bg-navy-800 text-editorial transition-all duration-300">
                                    Read JC's Story
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-navy-900 hover:bg-navy-900 hover:text-editorial transition-colors">
                                    Contact Ciera
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Full-width testimony section */}
            <section className="bg-navy-900 text-editorial py-24 md:py-32 relative overflow-hidden" data-testid="founder-quote">
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
                {/* Animated background pulse */}
                <motion.div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-coral/10 blur-3xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 relative">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-8">
                            <Sparkles className="w-6 h-6 text-gold" />
                            <p className="overline text-gold">In Her Own Words</p>
                        </div>
                        <blockquote className="font-serif italic text-2xl md:text-3xl lg:text-4xl text-editorial leading-snug max-w-4xl">
                            "The longest eight minutes of my life were spent performing CPR on someone I loved. When I heard a knock at the door after screaming for help, I thought help had finally arrived. Instead, it was the hotel manager and an employee. I begged them to help me. I asked if they knew CPR. I asked if there was an AED accessible."
                        </blockquote>
                        <blockquote className="font-serif italic text-2xl md:text-3xl text-editorial/85 leading-snug max-w-4xl mt-6">
                            "Both told me they could not help me and that there was no AED available. They just stood there as I begged for help. I went back to JC to continue CPR until first responders arrived. They watched me lose someone I loved and can never get back."
                        </blockquote>
                        <blockquote className="font-serif italic text-2xl md:text-3xl text-editorial/85 leading-snug max-w-4xl mt-6">
                            "In that moment I felt <span className="text-coral not-italic font-medium">helpless. Defeated.</span> No one should ever have to face a cardiac emergency alone without access to trained staff or life-saving equipment."
                        </blockquote>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.5 }}
                            className="font-serif text-4xl md:text-5xl text-gold mt-10 italic"
                        >
                            "Every second matters."
                        </motion.p>
                        <div className="mt-8 flex items-center gap-4">
                            <div className="w-16 h-px bg-gold" />
                            <div>
                                <p className="font-medium text-editorial">Ciera Gingery</p>
                                <p className="text-sm uppercase tracking-[0.2em] text-coral">Founder of JC Law</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <PetitionCTA />

            {/* Board Members / Leadership Team */}
            <section className="bg-editorial py-24 md:py-28 relative overflow-hidden" data-testid="board-members">
                <div className="absolute inset-0 bg-noise opacity-20 pointer-events-none" />
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
                    <Reveal>
                        <div className="text-center mb-16">
                            <p className="overline mb-4 text-coral">Leadership Team</p>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-navy-900">
                                Meet Our <span className="text-coral">Board</span>
                            </h2>
                            <div className="flex items-center justify-center gap-4 mt-6 max-w-md mx-auto">
                                <div className="flex-1 h-px bg-coral/40" />
                                <Heart className="w-4 h-4 text-coral animate-heartbeat" fill="currentColor" />
                                <div className="flex-1 h-px bg-coral/40" />
                            </div>
                            <p className="mt-6 text-navy-700/80 max-w-2xl mx-auto leading-relaxed">
                                The dedicated leaders working alongside Ciera to advance our mission of CPR awareness, AED accessibility, and emergency preparedness.
                            </p>
                        </div>
                    </Reveal>

                    <div className="grid sm:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto">
                        {BOARD_MEMBERS.map((member, idx) => (
                            <motion.div
                                key={member.email}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.8, delay: idx * 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
                                className="group bg-white shadow-lg hover:shadow-2xl transition-shadow duration-500"
                                data-testid={`board-member-${member.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="relative overflow-hidden bg-editorial-muted">
                                    <motion.div
                                        className="absolute -inset-2 border border-gold/40 -z-10"
                                        initial={{ scale: 0.95, opacity: 0 }}
                                        whileInView={{ scale: 1, opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.8, delay: 0.3 + idx * 0.1 }}
                                    />
                                    <img
                                        src={member.image}
                                        alt={`${member.name} — ${member.title}, JC Law Foundation`}
                                        className="w-full h-[420px] object-cover object-top transition-transform duration-[1500ms] group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-7 md:p-8 text-center">
                                    <h3 className="font-serif text-2xl md:text-3xl text-navy-900 leading-tight">
                                        {member.name}
                                    </h3>
                                    <p className="overline mt-2 text-coral">{member.title}</p>
                                    <div className="h-px w-12 bg-coral/50 mx-auto my-5" />
                                    <ul className="space-y-3 text-sm">
                                        <li className="flex items-center justify-center gap-2.5 text-navy-700/85 hover:text-coral transition-colors">
                                            <Mail size={15} className="text-coral flex-shrink-0" />
                                            <a href={`mailto:${member.email}`} className="hover:underline break-all">
                                                {member.email}
                                            </a>
                                        </li>
                                        <li className="flex items-center justify-center gap-2.5 text-navy-700/85 hover:text-coral transition-colors">
                                            <Phone size={15} className="text-coral flex-shrink-0" />
                                            <a href={`tel:${member.phoneRaw}`} className="hover:underline">
                                                {member.phone}
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
